import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Layout } from "providers/Layout";
import { useState } from "react";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { LayoutHeader } from "./components/layout-header";
import { NotificationCard } from "./components/NotificationCard";
import { NotificationGrouper } from "./helpers/grouper";
import { Notification, NotificationKind } from "./types";
import { HugeiconsIcon } from "@hugeicons/react";
import { Notification01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Card } from "shared/components/Card";

const grouper = new NotificationGrouper({
  locale: "en-US",
  weekStartsOn: 1,
  keyOf: (it) => String(it.id ?? `${it.created_at}|${it.user?.id ?? ""}`)
});

const notificationApi = ({
  kind,
  unreadOnly
}: {
  kind: NotificationKind;
  unreadOnly: boolean;
}) => {
  return instance
    .get("/portal/v1/notifications", {
      params: {
        kind,
        unread_only: unreadOnly
      }
    })
    .then((res) => res.data);
};

const unreadNotificationCountApi = ({ kind }: { kind: NotificationKind }) => {
  return instance
    .get("/portal/v1/notifications/unread_count?", {
      params: {
        kind
      }
    })
    .then((res) => res.data);
};

export const BoomNotificationView = () => {
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      tab: NotificationKind.Mention
    },
    ["tab"]
  );

  const { data: notifications } = useQuery<Notification[]>({
    queryKey: ["NOTIFICATIONS", searchParams.tab, unreadOnly],
    queryFn: () =>
      notificationApi({
        kind: searchParams.tab as NotificationKind,
        unreadOnly
      })
  });
  const { data: unread_notification_count } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", searchParams.tab],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: searchParams.tab as NotificationKind
      })
  });

  const groupedNotifications = grouper.group(notifications || []);
  return (
    <Layout
      headerClassName="border-b border-alpha-100"
      header={
        <LayoutHeader
          unreadTotalCount={unread_notification_count?.total_unread || 0}
          tab={searchParams.tab}
          onTabChange={(tab) => setSearchParams({ tab, ...searchParams })}
          unreadOnly={unreadOnly}
          toggleUnreadOnly={() => setUnreadOnly((prev) => !prev)}
        />
      }
    >
      <div className="mt-6 space-y-6">
        {groupedNotifications.length > 0 ? (
          <div className="mt-6 space-y-6">
            {groupedNotifications.map((group) => (
              <div key={group.name}>
                <h2 className="text-base font-medium mb-4 text-content-400">
                  {group.name}
                </h2>
                <Card className="overflow-hidden divide-y divide-alpha-40">
                  {group.items.map((item) => {
                    return (
                      <NotificationCard
                        key={item.id}
                        id={item.id}
                        authorName={
                          typeof item.author === "string"
                            ? item.author
                            : item.author.name
                        }
                        readAt={item.read_at}
                        richText={item.rich_text}
                        type={item.type}
                        url={item.url}
                        metadata={item.metadata}
                        createAt={item.created_at}
                      />
                    );
                  })}
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-auto flex-col items-center justify-center">
            <div className="mb-6 flex h-[64px] w-[64px] items-center justify-center rounded-full bg-alpha-40 p-5">
              <HugeiconsIcon
                icon={Notification01Icon}
                size={24}
                className="text-content-500"
              />
            </div>
            <p className="text-sm font-medium text-content-300">
              No notifications yet
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};
